/**
 * Noise suppression (Settings › Voice, 2026-11-01), as Discord's Krisp
 * option: Off (the raw microphone), Standard (the browser's own, the
 * default) or Strong (RNNoise in an AudioWorklet, voice/rnnoise.ts). Strong
 * turns the browser's own off, as Discord does with Krisp, and needs the
 * `noise_suppression` flag; while the flag is off, or where the browser has
 * no AudioWorklet or refuses the wasm, it acts as Standard.
 *
 * Measured cost (scripts/bench-rnnoise.mjs: the shipped worklet on 30 s of
 * 48 kHz audio, 2026-11-01, the dev PC under load): 7.1 ms of CPU per second
 * of audio with the SIMD wasm (0.7 % of one core), 11.3 ms without SIMD
 * (1.1 %). The wasm (150 KB) loads only when Strong is used.
 */
import type { NoiseSuppression } from '@neosocial/protocol';

/** A processed microphone track and how to end the processing. */
export interface Denoiser {
  track: MediaStreamTrack;
  close(): void;
}

/** What voice sends: the track, the noise suppression it really has, and the stop of both. */
export interface ProcessedMic {
  track: MediaStreamTrack;
  /** What runs: Strong that failed to start runs as Standard. */
  mode: NoiseSuppression;
  stop(): void;
}

/** The `getUserMedia` constraints for a mode: only Standard asks the browser to suppress. */
export function micConstraints(mode: NoiseSuppression): MediaTrackConstraints {
  return { noiseSuppression: mode === 'standard' };
}

/** The mode that applies: Strong only while it is allowed (the flag) and possible here. */
export function effectiveNoise(
  mode: NoiseSuppression,
  strongAllowed: boolean,
  supported: boolean = strongSupported(),
): NoiseSuppression {
  return mode === 'strong' && !(strongAllowed && supported) ? 'standard' : mode;
}

/** The browser can run Strong: Web Audio with AudioWorklet, and WebAssembly. */
export function strongSupported(): boolean {
  return (
    typeof AudioContext !== 'undefined' &&
    typeof AudioWorkletNode !== 'undefined' &&
    typeof WebAssembly !== 'undefined'
  );
}

type DenoiserFactory = (raw: MediaStreamTrack) => Promise<Denoiser>;

let factory: () => Promise<DenoiserFactory> = () =>
  import('./rnnoise').then((m) => m.createRnnoise);

/** Tests swap RNNoise out. */
export function setDenoiserFactory(f: DenoiserFactory | null): void {
  factory = f ? () => Promise.resolve(f) : () => import('./rnnoise').then((m) => m.createRnnoise);
}

/**
 * The microphone voice sends for `mode` (`raw` was opened with
 * `micConstraints(mode)`). Strong runs RNNoise on it; if that cannot start,
 * the browser's own suppression is turned on instead and Standard is what
 * runs (never silence, never the raw microphone by surprise).
 */
export async function processMic(
  raw: MediaStreamTrack,
  mode: NoiseSuppression,
): Promise<ProcessedMic> {
  if (mode !== 'strong') return { track: raw, mode, stop: () => raw.stop() };
  try {
    const d = await (await factory())(raw);
    return {
      track: d.track,
      mode: 'strong',
      stop() {
        d.close();
        raw.stop();
      },
    };
  } catch (e) {
    console.warn('Strong noise suppression could not start; using Standard:', e);
    await raw.applyConstraints?.({ noiseSuppression: true }).catch(() => undefined);
    return { track: raw, mode: 'standard', stop: () => raw.stop() };
  }
}
